import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, Home, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const PaymentCancel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    // Log cancellation for analytics
    console.log('💳 Payment cancelled for booking:', bookingId);
  }, [bookingId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center"
          >
            <XCircle className="w-12 h-12 text-orange-600" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-gray-800 mb-4"
          >
            {language === 'ar' ? 'تم إلغاء الدفع' : language === 'fr' ? 'Paiement annulé' : 'Payment Cancelled'}
          </motion.h1>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-gray-600 mb-8 leading-relaxed"
          >
            {language === 'ar' 
              ? 'لا تقلق! لم يتم خصم أي مبلغ من حسابك. يمكنك المحاولة مرة أخرى في أي وقت.'
              : language === 'fr'
              ? "Ne vous inquiétez pas ! Aucun montant n'a été débité. Vous pouvez réessayer à tout moment."
              : "Don't worry! No charges were made to your account. You can try again anytime."}
          </motion.p>

          {/* Booking ID if available */}
          {bookingId && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-gray-50 rounded-lg p-4 mb-6"
            >
              <p className="text-sm text-gray-500 mb-1">
                {language === 'ar' ? 'رقم الحجز' : language === 'fr' ? 'Numéro de réservation' : 'Booking ID'}
              </p>
              <p className="text-sm font-mono text-gray-700 break-all">{bookingId}</p>
              <p className="text-xs text-gray-500 mt-2">
                {language === 'ar' 
                  ? 'احتفظ بهذا الرقم إذا كنت تريد إكمال الحجز لاحقاً'
                  : language === 'fr'
                  ? 'Conservez ce numéro si vous souhaitez finaliser plus tard'
                  : 'Keep this number if you want to complete the booking later'}
              </p>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            {/* Try Again / Contact Support */}
            <button
              onClick={() => navigate('/rooms')}
              className="w-full bg-[#D4AF37] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#B5952F] transition-all duration-300 flex items-center justify-center space-x-2 group"
            >
              <Home className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>
                {language === 'ar' ? 'تصفح الغرف' : language === 'fr' ? 'Parcourir les chambres' : 'Browse Rooms'}
              </span>
            </button>

            {/* Contact Support */}
            <button
              onClick={() => {
                // Open chatbot or navigate to contact
                const chatButton = document.querySelector('[data-testid="chatbot-button"]');
                if (chatButton) {
                  chatButton.click();
                }
              }}
              className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <MessageCircle className="w-5 h-5" />
              <span>
                {language === 'ar' ? 'تحدث معنا' : language === 'fr' ? 'Nous contacter' : 'Chat with Us'}
              </span>
            </button>

            {/* Back to Home */}
            <button
              onClick={() => navigate('/')}
              className="w-full text-gray-500 py-2 hover:text-gray-700 transition-colors text-sm"
            >
              {language === 'ar' ? 'العودة للصفحة الرئيسية' : language === 'fr' ? "Retour à l'accueil" : 'Back to Home'}
            </button>
          </motion.div>
        </div>

        {/* Additional Help */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 text-center text-sm text-gray-500"
        >
          {language === 'ar' 
            ? 'هل تحتاج مساعدة؟ نحن هنا لمساعدتك على مدار الساعة.'
            : language === 'fr'
            ? "Besoin d'aide ? Nous sommes là 24/7 pour vous aider."
            : 'Need help? We\'re here 24/7 to assist you.'}
        </motion.div>
      </motion.div>
    </div>
  );
};
